import { motion } from 'framer-motion';
import {
  Banknote,
  Fingerprint,
  LineChart,
  Users,
  Workflow,
  Zap,
} from 'lucide-react';
import { AnimatedSection } from './AnimatedSection';
import { usePublicConfig } from '../context';

const getFeatures = (chamaName: string) => [
  {
    icon: Workflow,
    title: 'Automated contributions',
    description:
      'Track M-Pesa payments automatically. No more manual reconciliation or chasing members on WhatsApp.',
  },
  {
    icon: Fingerprint,
    title: 'Complete transparency',
    description:
      'Every transaction logged and visible. Your chama committee stays accountable—just like {chamaName} demo shows.',
  },
  {
    icon: Banknote,
    title: 'Smart payouts',
    description:
      'Automatic payout rotation, instant notifications, and zero confusion about who gets paid next.',
  },
  {
    icon: LineChart,
    title: 'Real-time dashboards',
    description:
      'See contributions, penalties, and balances at a glance. No spreadsheets, no guesswork.',
  },
  {
    icon: Zap,
    title: 'SMS reminders',
    description:
      'Automatic contribution reminders and payment confirmations. Members stay informed without manual follow-ups.',
  },
  {
    icon: Users,
    title: 'Member portals',
    description:
      'Every member gets their own dashboard to view contributions, history, and upcoming payouts—24/7 access.',
  },
];

export const Features = () => {
  const { config: publicConfig } = usePublicConfig();
  const chamaName = publicConfig?.name ?? 'LoopFund';
  const features = getFeatures(chamaName);

  return (
  <AnimatedSection id="features" className="section-padding">
    <div className="mx-auto flex max-w-6xl flex-col gap-12 text-center">
      <div className="space-y-4">
        <span className="rounded-full border border-slate-200 px-5 py-2 text-xs uppercase tracking-[0.35em] text-slate-500 dark:border-slate-700 dark:text-slate-300">
          Why switch to LoopFund
        </span>
        <h2 className="font-display text-3xl text-slate-900 md:text-4xl dark:text-white">
          Everything your chama needs in one platform
        </h2>
        <p className="mx-auto max-w-2xl text-base text-slate-500 dark:text-slate-300">
          See how {chamaName} runs smoothly with automated tracking, transparent records, and zero manual work. 
          This could be YOUR chama—ditch the WhatsApp chaos and upgrade today.
        </p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
        {features.map(({ icon: Icon, title, description }, index) => (
          <motion.div
            key={title}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.6,
              delay: index * 0.08,
              ease: [0.16, 1, 0.3, 1],
            }}
            viewport={{ once: true, amount: 0.5 }}
            className="group h-full rounded-3xl border border-slate-200 bg-slate-50 p-6 text-left shadow-sm transition-transform duration-300 hover:-translate-y-1 dark:border-slate-700 dark:bg-slate-800"
          >
            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-100 text-brand-700 dark:bg-slate-700 dark:text-brand-200">
              <Icon className="h-5 w-5" />
            </div>
            <h3 className="font-display text-xl text-slate-900 dark:text-white">
              {title}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-500 dark:text-slate-300">
              {description}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  </AnimatedSection>
  );
};
